import { useEffect, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { searchFoodsRemotely, type FoodSearchResult } from "../../application/remote-meal-parser";

function trailingWord(value: string): { query: string; start: number } | null {
  const match = /[\p{L}\p{M}]+$/u.exec(value);
  return match ? { query: match[0], start: match.index } : null;
}

export function replaceTrailingFoodWord(value: string, selectedName: string): string {
  const match = trailingWord(value);
  return match ? `${value.slice(0, match.start)}${selectedName}` : selectedName;
}

export function FoodAutocompleteInput({ id, value, onChange, placeholder, disabled = false, className }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  className: string;
}) {
  const [suggestions, setSuggestions] = useState<FoodSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const term = trailingWord(value);
  const query = term?.query ?? "";
  const listId = `${id}-suggestions`;

  useEffect(() => {
    if (disabled || query.length < 3) {
      setSuggestions([]);
      setSearching(false);
      setSearchError(false);
      setActiveSuggestion(-1);
      return;
    }
    let current = true;
    setSuggestions([]);
    setSearching(true);
    setSearchError(false);
    setActiveSuggestion(-1);
    const timer = window.setTimeout(() => {
      void searchFoodsRemotely(query).then(results => {
        if (current) setSuggestions(results.slice(0, 15));
      }).catch(() => {
        if (current) { setSuggestions([]); setSearchError(true); }
      }).finally(() => { if (current) setSearching(false); });
    }, 220);
    return () => { current = false; window.clearTimeout(timer); };
  }, [disabled, query]);

  const select = (food: FoodSearchResult) => {
    const name = food.matchedName || food.nutrition?.name || food.name;
    onChange(replaceTrailingFoodWord(value, name));
    setSuggestions([]);
    setActiveSuggestion(-1);
  };
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion(index => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp" && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion(index => index <= 0 ? suggestions.length - 1 : index - 1);
    } else if (event.key === "Enter" && activeSuggestion >= 0) {
      event.preventDefault();
      const food = suggestions[activeSuggestion];
      if (food) select(food);
    } else if (event.key === "Escape") {
      setSuggestions([]);
    }
  };
  const showList = !disabled && query.length >= 3 && (searching || searchError || suggestions.length > 0 || !searching);

  return <div className="food-autocomplete">
    <input id={id} className={className} type="text" role="combobox" aria-autocomplete="list" aria-controls={listId} aria-expanded={showList} aria-activedescendant={activeSuggestion >= 0 ? `${id}-option-${activeSuggestion}` : undefined} value={value} onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)} onKeyDown={onInputKeyDown} placeholder={placeholder} autoComplete="off" disabled={disabled} />
    {showList && <div id={listId} className="food-autocomplete-list" role="listbox" aria-label="Подходящие продукты">
      {searching && <p role="status">Ищу продукты…</p>}
      {searchError && <p role="alert">Не удалось загрузить подсказки.</p>}
      {!searching && !searchError && suggestions.map((food, index) => <button id={`${id}-option-${index}`} type="button" role="option" aria-selected={activeSuggestion === index} key={`${food.id}:${food.name}`} onMouseDown={event => event.preventDefault()} onClick={() => select(food)}><span>{food.matchedName || food.name}</span>{food.attribution && <small>{food.attribution}</small>}</button>)}
      {!searching && !searchError && !suggestions.length && <p>Совпадений в каталоге нет. Можно оставить название и разобрать запись.</p>}
    </div>}
  </div>;
}
